import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import type { GetGlobalServiceInformationTemplatesNormalized } from '@messenger/types'
import {
  itemAtomFamily,
  itemSelector,
  selectedItemAtom,
  unsavedChangesAtom,
} from 'atoms'
import { isSavingAtom } from '@/atoms/_editorAtoms'
import { useEditorApplyTextBoxTemplate } from '@/hooks/editor/useEditorApplyTextBoxTemplate'
import { useEditorHtmlConversionContext } from '@/hooks/editor/useEditorHtmlConversionContext'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useIsInternalAdmin } from '@/hooks/utility/useIsInternalAdmin'
import { useSetToast } from 'hooks'
import {
  editorTextBlocksFromHtml,
  editorTextBoxConversionToastText,
  isEditorTextBoxItemType,
  parseEditorServiceTemplateContent,
  type EditorTextBlocksFromHtmlResult,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'
import * as S from '../InsertMenu/SlideoutMenu/SlideoutMenu.styled'
import {
  serviceInformationTemplatesAtom,
  swapServiceTemplateSignalAtom,
} from '@/atoms/_editorAtoms'
import ServiceTemplatesMenu from './ServiceTemplatesMenu'
import { useTranslation } from 'react-i18next'

type ServiceTemplatesSwapMenuProps = {
  show: boolean
  setShow: (show: boolean) => void
}

export const ServiceTemplatesSwapMenu = ({
  show,
  setShow,
  ...props
}: ServiceTemplatesSwapMenuProps) => {
  const { t, i18n } = useTranslation()
  const handleError = useErrorHandler('ServiceTemplatesSwapMenu')
  const isInternalAdmin = useIsInternalAdmin()
  const setToast = useSetToast()
  const applyTextBoxTemplate = useEditorApplyTextBoxTemplate()

  const templates = useRecoilValue(serviceInformationTemplatesAtom)
  const selectedItem = useRecoilValue(selectedItemAtom)
  // A swap writes the line, so it waits for the Save that is already writing
  // it — the same rule the header's Save button and the layout drawers follow.
  const isSaving = useRecoilValue(isSavingAtom)

  const [stableSelectedItem, setStableSelectedItem] = useState(selectedItem)

  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)

  useEffect(() => {
    if (selectedItem) {
      setStableSelectedItem(selectedItem)
    }
  }, [selectedItem])

  const targetItem = useRecoilValue(itemAtomFamily(stableSelectedItem ?? ''))
  const targetIsEditorTextBox = isEditorTextBoxItemType(
    (targetItem as { type?: unknown } | null | undefined)?.type,
  )

  // A Lotus box takes every template: an editor-format one as it is, a
  // legacy one converted. A TinyMCE box takes legacy templates only.
  const compatibleTemplates = useMemo(
    () =>
      targetIsEditorTextBox
        ? templates
        : templates.filter(
            (template) =>
              parseEditorServiceTemplateContent(template.template) === null,
          ),
    [templates, targetIsEditorTextBox],
  )

  const readSwapContext = useEditorHtmlConversionContext()

  const updateTextValue = useRecoilCallback(
    ({ set }) =>
      (itemId: string, value: string) => {
        if (!itemId) return
        const textValueSelector = itemSelector({
          id: itemId,
          path: 'text.value',
        })
        set(textValueSelector, value)
        set(swapServiceTemplateSignalAtom, Date.now())
      },
    [],
  )

  const handleSwapTemplate = useCallback(
    async (template: GetGlobalServiceInformationTemplatesNormalized) => {
      if (!stableSelectedItem) {
        setToast({
          text: t(
            'editor.serviceTemplates.pleaseSelectATextItemToSwapTemplates',
          ),
          icon: 'error',
        })
        return
      }

      if (targetIsEditorTextBox) {
        const content = parseEditorServiceTemplateContent(template.template)
        const { pageId, caseServiceIds } = readSwapContext()
        // An arrangement applies as it is; an HTML template is converted.
        let converted: EditorTextBlocksFromHtmlResult
        try {
          converted =
            content !== null
              ? { ok: true, blocks: content.blocks, report: [] }
              : editorTextBlocksFromHtml(template.template, caseServiceIds)
        } catch (error) {
          handleError(
            t('editor.serviceTemplates.failedToSwapTemplate'),
            error as Error,
          )
          return
        }
        if (!converted.ok) {
          logger.info({
            message: 'editor_swap_text_box_template_refused',
            category: EventCategory.INSERT,
            data: {
              pageId,
              boxId: stableSelectedItem,
              templateId: template.id,
              reason: converted.refusal.reason,
            },
          })
          setToast({
            text: t('editor.serviceTemplates.failedToSwapTemplate'),
            icon: 'error',
          })
          return
        }
        const applied = await applyTextBoxTemplate(
          stableSelectedItem,
          converted.blocks,
        )
        if (!applied) return
        const { report } = converted
        if (report.length > 0)
          logger.info({
            message: 'editor_swap_text_box_template_report',
            category: EventCategory.INSERT,
            data: {
              pageId,
              boxId: stableSelectedItem,
              templateId: template.id,
              report,
            },
          })
        // Internal staff see what a converted template lost in place of the
        // swap's own toast; everyone else sees the swap's toast alone.
        setToast(
          report.length > 0 && isInternalAdmin
            ? {
                text: editorTextBoxConversionToastText(
                  report,
                  t,
                  i18n.resolvedLanguage,
                ),
              }
            : {
                text: t('editor.serviceTemplates.templateSwappedSuccessfully'),
                icon: 'success',
              },
        )
        setShow(false)
        return
      }

      try {
        updateTextValue(stableSelectedItem, template.template)
        setToast({
          text: t('editor.serviceTemplates.templateSwappedSuccessfully'),
          icon: 'success',
        })
        setShow(false)
        setUnsavedChanges(true)
      } catch (error) {
        const err = error as { message?: string }
        handleError(
          err?.message || t('editor.serviceTemplates.failedToSwapTemplate'),
          error as Error,
        )
      }
    },
    [
      stableSelectedItem,
      targetIsEditorTextBox,
      readSwapContext,
      isInternalAdmin,
      applyTextBoxTemplate,
      updateTextValue,
      setToast,
      setShow,
      handleError,
      setUnsavedChanges,
      t,
      i18n,
    ],
  )

  return (
    <S.ModalSlideout
      right="250px"
      left="250px"
      position="absolute"
      width={show ? '470px' : '0'}
      height={show ? '100%' : '0'}
      transition="transform 0.25s ease-in-out, opacity 0.25s"
      background="white"
      opacity={show ? 1 : 0}
      translateY={show ? '0px' : '20px'}
      borderTop="1px solid"
      borderColor="gray4"
      zindex="10000"
      data-swap-target-kind={String(targetIsEditorTextBox)}
      data-swap-selected={stableSelectedItem || 'none'}
      data-swap-target-type={String(
        (targetItem as { type?: unknown } | null | undefined)?.type ?? 'none',
      )}
      {...props}
    >
      {show && (
        <ServiceTemplatesMenu
          templates={compatibleTemplates}
          onTemplateSelect={handleSwapTemplate}
          onClose={setShow}
          title={t('editor.serviceTemplates.chooseAServiceTemplate')}
          allowActions={false}
          disabled={isSaving}
        />
      )}
    </S.ModalSlideout>
  )
}

export default ServiceTemplatesSwapMenu
